/**
 * Google's sign-in popup, reduced to one promise.
 *
 * Google Identity Services' *code client* in popup mode: the popup asks for
 * consent and hands back a one-time `code`, which the API redeems with the
 * client secret. The button stays ours - no Google-rendered iframe - so it can
 * sit beside the WhatsApp one in the NeonCarts style.
 *
 * The script is loaded on first use rather than on every page load, so a
 * visitor who signs in with a password never fetches it.
 */

const SCRIPT_URL = 'https://accounts.google.com/gsi/client';

interface CodeResponse {
  code?: string;
  error?: string;
  error_description?: string;
}

interface CodeClientError {
  type: 'popup_failed_to_open' | 'popup_closed' | 'unknown';
}

interface CodeClient {
  requestCode: () => void;
}

interface GoogleAccounts {
  oauth2: {
    initCodeClient: (config: {
      client_id: string;
      scope: string;
      ux_mode: 'popup';
      callback: (response: CodeResponse) => void;
      error_callback?: (error: CodeClientError) => void;
    }) => CodeClient;
  };
}

declare global {
  interface Window {
    google?: { accounts?: GoogleAccounts };
  }
}

/** Why the popup produced no code. `cancelled` is the person closing it. */
export class GoogleSignInError extends Error {
  readonly reason: 'cancelled' | 'blocked' | 'unavailable' | 'denied';

  constructor(reason: GoogleSignInError['reason'], message: string) {
    super(message);
    this.name = 'GoogleSignInError';
    this.reason = reason;
  }
}

let loading: Promise<GoogleAccounts> | null = null;

function loadScript(): Promise<GoogleAccounts> {
  if (window.google?.accounts) return Promise.resolve(window.google.accounts);
  if (loading) return loading;

  loading = new Promise<GoogleAccounts>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google?.accounts) resolve(window.google.accounts);
      else reject(new Error('Google sign-in did not load.'));
    };
    script.onerror = () => reject(new Error('Google sign-in did not load.'));
    document.head.appendChild(script);
  }).catch(() => {
    // A failed load (offline, an ad blocker) is retried on the next click
    // rather than remembered for the life of the page.
    loading = null;
    throw new GoogleSignInError(
      'unavailable',
      'We could not reach Google. Check your connection or disable any blocker, then try again.',
    );
  });
  return loading;
}

/** Warm the script up, e.g. when the button first renders. Never throws. */
export function preloadGoogleIdentity(): void {
  loadScript().catch(() => undefined);
}

/**
 * Open Google's consent popup and resolve with the one-time code.
 *
 * Must be called straight from a click, or the browser blocks the popup.
 */
export async function requestGoogleCode(clientId: string): Promise<string> {
  const accounts = await loadScript();

  return new Promise<string>((resolve, reject) => {
    const client = accounts.oauth2.initCodeClient({
      client_id: clientId,
      scope: 'openid email profile',
      ux_mode: 'popup',
      callback: (response) => {
        if (response.code) {
          resolve(response.code);
          return;
        }
        reject(
          new GoogleSignInError(
            response.error === 'access_denied' ? 'cancelled' : 'denied',
            response.error === 'access_denied'
              ? 'Google sign-in was cancelled.'
              : 'Google did not complete the sign-in. Please try again.',
          ),
        );
      },
      error_callback: (error) => {
        if (error.type === 'popup_failed_to_open') {
          reject(
            new GoogleSignInError(
              'blocked',
              'Your browser blocked the Google window. Allow pop-ups for this site and try again.',
            ),
          );
          return;
        }
        reject(new GoogleSignInError('cancelled', 'Google sign-in was cancelled.'));
      },
    });
    client.requestCode();
  });
}
